import Vue from 'vue'
import mutations from './mutations'
import levels from '@/api/levels'
import { notify } from '@/utils/error'
import { arrayToObject } from '@/utils/object'

import { loadItemsBuilder, loadItemBuilder, saveItemBuilder, deleteItemBuilder } from '../crudFactory'
const apiEndpoint = 'levels'

function recursiveOrderUpdate (updates, businessUnitId) {
  return new Promise((resolve, reject) => {
    const currentUpdate = updates[0]
    const remainingUpdates = updates.slice(1)
    levels.setSortOrder({ businessUnitId, levelId: currentUpdate.id, sortOrder: currentUpdate.sort_order })
      .then(() => {
        if (remainingUpdates.length) {
          recursiveOrderUpdate(remainingUpdates, businessUnitId)
        }
        resolve()
      })
      .catch(error => {
        reject(error)
      })
  })
}

function recursiveOrderUpdateCategories (updates, businessUnitId, levelId) {
  return new Promise((resolve, reject) => {
    const currentUpdate = updates[0]
    const remainingUpdates = updates.slice(1)
    levels.setCategorySortOrder({ businessUnitId, levelId, categoryId: currentUpdate.id, sortOrder: currentUpdate.sort_order })
      .then(() => {
        if (remainingUpdates.length) {
          recursiveOrderUpdateCategories(remainingUpdates, businessUnitId, levelId)
        }
        resolve()
      })
      .catch(error => {
        reject(error)
      })
  })
}

export default {
  namespaced: true,

  state: {
    items: {},
    categories: {},
    articles: {}
  },

  actions: {
    loadItems: loadItemsBuilder({ apiEndpoint }),
    loadItem: loadItemBuilder({ apiEndpoint }),
    saveItem: saveItemBuilder({ apiEndpoint }),
    deleteItem: deleteItemBuilder({ apiEndpoint }),
    loadCategories ({ state, commit, rootState }, levelId) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.getAllCategories({ businessUnitId, levelId })
          .then(response => {
            const items = arrayToObject(response.data.items)
            const categories = { ...state.categories }
            categories[levelId] = items
            commit('setElements', { type: 'categories', elements: categories })
            resolve(categories)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    addCategory ({ commit, dispatch, rootState }, { levelId, categoryId }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.saveCategory({ businessUnitId, levelId, categoryId })
          .then(response => {
            dispatch('categories/loadItem', categoryId, { root: true })
              .then(category => {
                commit('setCategory', { levelId, category })
                resolve(category)
              })
              .catch(error => {
                notify(error)
                reject(error)
              })
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    deleteCategory ({ commit, rootState }, { levelId, categoryId }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.deleteCategory({ businessUnitId, levelId, categoryId })
          .then(response => {
            commit('removeCategory', { levelId, categoryId })
            resolve()
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    loadArticles ({ state, commit, rootState }, levelId) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.getAllArticles({ businessUnitId, levelId })
          .then(response => {
            const items = arrayToObject(response.data.items)
            const articles = { ...state.articles }
            articles[levelId] = items
            commit('setElements', { type: 'articles', elements: articles })
            resolve(articles)
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    addArticle ({ commit, dispatch, rootState }, { levelId, articleId }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.saveArticle({ businessUnitId, levelId, articleId })
          .then(response => {
            dispatch('articles/loadItem', articleId, { root: true })
              .then(article => {
                commit('setArticle', { levelId, article })
                resolve(article)
              })
              .catch(error => {
                notify(error)
                reject(error)
              })
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    deleteArticle ({ commit, rootState }, { levelId, articleId }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        levels.deleteArticle({ businessUnitId, levelId, articleId })
          .then(response => {
            commit('removeArticle', { levelId, articleId })
            resolve()
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    updateOrder ({ rootState }, updates) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        recursiveOrderUpdate(updates, businessUnitId)
          .then(() => {
            resolve()
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    },
    updateCategoriesOrder ({ rootState }, { updates, levelId }) {
      return new Promise((resolve, reject) => {
        const businessUnitId = rootState.businessUnits.currentBusinessUnit.id
        recursiveOrderUpdateCategories(updates, businessUnitId, levelId)
          .then(() => {
            resolve()
          })
          .catch(error => {
            notify(error)
            reject(error)
          })
      })
    }
  },

  mutations: {
    ...mutations,
    setCategory (state, { levelId, category }) {
      Vue.set(state.categories[levelId], category.id, category)
    },
    setArticle (state, { levelId, article }) {
      Vue.set(state.articles[levelId], article.id, article)
    },
    removeCategory (state, { levelId, categoryId }) {
      Vue.delete(state.categories[levelId], categoryId)
    },
    removeArticle (state, { levelId, articleId }) {
      Vue.delete(state.articles[levelId], articleId)
    }
  }
}
